/* eslint-disable no-alert */
import React from "react";
import * as yup from "yup";
import "data/yup-validation";
import { Formik, Form } from "formik";
import { get, flatten } from "lodash";
import { gql } from "graphql-tag";
import { useMutation, ApolloError } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { US_PHONE_MASK } from "utils/phone";
import { cleanApolloErrorMessage } from "utils/apollo";
import logger from "services/logger";
import { Button } from "components/haven-design/buttons";
import { InputWithErrorLabel, MaskedInputWithErrorLabel } from "components/haven-design/form/inputs";
import { createClientUser } from "types/gql-types";

const noContactMsg = "A phone or email address is required";

const formSchema = yup.object().shape({
  phone: yup
    .string()
    .requireOneOfFields(["email", "phone"], noContactMsg)
    .validPhoneNumber("Invalid phone number"),
  email: yup
    .string()
    .requireOneOfFields(["email", "phone"], noContactMsg)
    .matches(/@/, "Requires a valid Email"),
  password: yup.string().requireSibling("email").validPassword(),
});

const initialData = {
  email: "",
  phone: "",
  password: "",
};

type Props = {
  firstName: string | null;
  lastName: string | null;
  email?: string;
  phone?: string;
  requestCode?: string;
}

const CREATE_USER_GQL = gql`
  mutation createClientUser($data: UserInput!, $requestCode: String) {
    createUser(data: $data, requestCode: $requestCode) {
      id
    }
  }
`;

enum CreateAccountError {
  ExistingAccount = "User account(s) already exist",
}
const FormInput: React.FC<{loading: boolean, values: typeof initialData}> =
({ loading, values }) => (
  <Form>
    <MaskedInputWithErrorLabel
      label="Cell Phone Number"
      name="phone"
      mask={US_PHONE_MASK}
      guide={false}
    />

    <InputWithErrorLabel label="Email Address" name="email" type="text" />

    {values.email && (
      <InputWithErrorLabel
        label="Choose a Password for Your Account"
        name="password"
        type="password"
      />
    )}

    <Button type="submit" size="L" color="primary" disabled={loading}>
        Create Account
    </Button>
  </Form>);

const CreateAccountForm: React.FC<Props> = (props) => {
  const navigate = useNavigate();
  const [createUser, { loading }] = useMutation<createClientUser>(CREATE_USER_GQL);
  return (
    <Formik
      initialValues={initialData}
      validationSchema={formSchema}
      onSubmit={(form) => {
        const userInput = {
          roles: ["client"],
          firstName: props.firstName,
          lastName: props.lastName,
          email: form.email.trim().toLowerCase() || null,
          phone: form.phone.replace(/[^\d]/g, "") || null,
          password: form.password.trim() || null,
        };

        createUser({ variables: { data: userInput, requestCode: props.requestCode } })
          .then(() => {
            navigate("/login");
          })
          .catch((error: ApolloError) => {
            switch (cleanApolloErrorMessage(error.message) as CreateAccountError) {
              case CreateAccountError.ExistingAccount: {
                const emails: string[][] = get(error, "graphQLErrors[0].details", []);
                return alert(
                  `The email and/or phone number provided is conflicting with one of
                   these existing email accounts: ${flatten(emails).join(", ")}.
                   Please contact support@havenconnect.com for assistance.`,
                );
              }
              default:
                logger.sendError(error);
                return alert("Unknown Error, please contact support at support@havenconnect.com");
            }
          });
      }}
    >
      {({ values }) => (
        <FormInput values={values} loading={loading} />
      )}
    </Formik>
  );
};

export default CreateAccountForm;
